import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { randomUUID } from '@/utils/uuid';

import { ResultadoBinario } from '@/types/enums';

import { BaermannPoolCard } from './BaermannPoolCard';
import type { PoolLocal } from './BaermannPoolCard';

interface BaermannPoolsSectionProps {
  pools: PoolLocal[];
  onChange: (pools: PoolLocal[]) => void;
  showErrors: boolean;
  disabled?: boolean;
  maxTotal?: number;
  hideMuestras?: boolean;
}

export function BaermannPoolsSection({
  pools,
  onChange,
  showErrors,
  disabled = false,
  maxTotal = 15,
  hideMuestras = false,
}: BaermannPoolsSectionProps): JSX.Element {
  const totalMuestras = pools.reduce((s, p) => s + p.numeros_muestras.length, 0);
  const positivos = pools.filter((p) => p.valor === ResultadoBinario.POSITIVO).length;
  const atMaxPools = pools.length >= 10;
  const totalExcedido = !hideMuestras && totalMuestras > maxTotal;

  function handlePoolChange(index: number, updated: Partial<PoolLocal>): void {
    const next = pools.map((p, i) => (i === index ? { ...p, ...updated } : p));
    onChange(next);
  }

  function handlePoolDelete(index: number): void {
    onChange(pools.filter((_, i) => i !== index));
  }

  function handleAddPool(): void {
    onChange([
      ...pools,
      { id: randomUUID(), numeros_muestras: [], valor: null, observacion: null },
    ]);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1">Pools</Typography>
          {!hideMuestras && (
            <Typography variant="body2" color={totalExcedido ? 'error' : 'text.secondary'}>
              {pools.length} pools · {totalMuestras} muestras totales (máx {maxTotal})
            </Typography>
          )}
        </Box>
        <Chip
          label={`${positivos} pos`}
          size="small"
          color={positivos > 0 ? 'success' : 'default'}
        />
      </Box>

      <Stack spacing={2}>
        {pools.map((pool, index) => {
          const allUsedInOtherPools = new Set(
            pools.flatMap((p, i) => (i === index ? [] : p.numeros_muestras)),
          );
          return (
            <BaermannPoolCard
              key={pool.id}
              pool={pool}
              poolIndex={index}
              onChange={(updated) => handlePoolChange(index, updated)}
              onDelete={() => handlePoolDelete(index)}
              canDelete={pools.length > 1}
              allUsedInOtherPools={allUsedInOtherPools}
              totalMuestras={totalMuestras}
              topeMuestrasTotal={maxTotal}
              showErrors={showErrors}
              disabled={disabled}
              hideMuestras={hideMuestras}
            />
          );
        })}
      </Stack>

      <Tooltip title={atMaxPools ? 'Máximo 10 pools alcanzado.' : ''} placement="top">
        <span>
          <Button
            variant="outlined"
            fullWidth
            disabled={atMaxPools || disabled}
            onClick={handleAddPool}
            sx={{ mt: 2 }}
          >
            + Agregar pool
          </Button>
        </span>
      </Tooltip>
    </Paper>
  );
}
